import React, { useEffect, useRef } from 'react';
import { Text, Flex, Box } from "@radix-ui/themes";
import Icon from '@mdi/react';
import { mdiClose, mdiAccountGroup, mdiChartDonut } from '@mdi/js';
import { useStore } from '../store/store';
import Chart from 'chart.js/auto';
import annotationPlugin from 'chartjs-plugin-annotation';

// Register the annotation plugin
Chart.register(annotationPlugin);

const SidebarAnalyticsCharacters = ({ onClose }) => {
  const [state] = useStore();
  const novel = state.currentNovel;
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  
  // Process character data for the chart
  useEffect(() => {
    if (!novel || !chartRef.current) return;
    
    // Destroy previous chart if it exists
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }
    
    // Create a map to count POV scenes per character
    const povSceneCounts = {};
    let totalPovScenes = 0;
    
    // Go through all chapters and scenes to count POV scenes
    novel.chapters?.forEach(chapter => {
      chapter.scenes?.forEach(scene => {
        if (scene.pov) {
          // Normalize POV name (trim whitespace, convert to lowercase for comparison)
          const povName = scene.pov.trim();
          
          // Skip empty POV names
          if (povName === '') return;
          
          // Increment the count for this POV character
          povSceneCounts[povName] = (povSceneCounts[povName] || 0) + 1;
          totalPovScenes++;
        }
      });
    });
    
    // If no POV scenes, don't create a chart
    if (totalPovScenes === 0) {
      return;
    }
    
    // Sort by count (most POV scenes first)
    const sortedPovEntries = Object.entries(povSceneCounts)
      .sort((a, b) => b[1] - a[1]);
    
    // Extract character names and counts for the chart
    const characterNames = sortedPovEntries.map(entry => entry[0]);
    const povCounts = sortedPovEntries.map(entry => entry[1]);
    
    // Calculate average POV scenes per character
    const avgPovScenes = totalPovScenes / characterNames.length;
    
    // Create a gradient for the bars
    const ctx = chartRef.current.getContext('2d');
    const gradient = ctx.createLinearGradient(0, 0, 0, 400);
    gradient.addColorStop(0, 'rgba(125, 99, 205, 0.8)');
    gradient.addColorStop(1, 'rgba(125, 99, 205, 0.2)');
    
    // Create the chart
    chartInstance.current = new Chart(chartRef.current, {
      type: 'bar',
      data: {
        labels: characterNames,
        datasets: [
          {
            label: 'POV Scenes',
            data: povCounts,
            backgroundColor: gradient,
            borderColor: 'rgba(125, 99, 205, 1)',
            borderWidth: 1
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: 'Number of Scenes',
              color: 'rgba(255, 255, 255, 0.8)'
            },
            ticks: {
              color: 'rgba(255, 255, 255, 0.6)',
              stepSize: 1
            },
            grid: {
              color: 'rgba(255, 255, 255, 0.1)'
            }
          },
          x: {
            ticks: {
              color: 'rgba(255, 255, 255, 0.6)'
            },
            grid: {
              color: 'rgba(255, 255, 255, 0.1)'
            }
          }
        },
        plugins: {
          legend: {
            labels: {
              color: 'rgba(255, 255, 255, 0.8)'
            }
          },
          annotation: {
            annotations: {
              line1: {
                type: 'line',
                yMin: avgPovScenes,
                yMax: avgPovScenes,
                borderColor: 'rgba(255, 255, 255, 0.7)',
                borderWidth: 2,
                borderDash: [6, 6],
                label: {
                  content: `Average: ${avgPovScenes.toFixed(1)} scenes`,
                  enabled: true,
                  position: 'end',
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  color: 'white',
                  font: {
                    size: 12
                  },
                  padding: 6
                }
              }
            }
          }
        }
      }
    });
    
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [novel]);
  
  return (
    <div className='sidebar-sidebar analytics-sidebar sidebar-analytics-characters'>
      <div style={{textAlign: 'right', cursor: 'pointer'}} onClick={onClose}>
        <Icon path={mdiClose} color={'#FFFFFF50'} size={1} />
      </div>
      
      <Flex direction="column" gap="3">
        <Text size="4" weight="bold" style={{color: 'white'}}>
          Character Analysis
        </Text>
        
        <Box p="3" style={{backgroundColor: '#3c4748', borderRadius: '8px'}}>
          <Text size="2" style={{color: 'white', opacity: 0.9}}>
            {novel?.title} has {novel?.characters?.length || 0} characters.
          </Text>
          
          <Box mt="4" style={{height: '400px', position: 'relative'}}>
            <canvas ref={chartRef}></canvas>
          </Box>
          
          <Text size="2" style={{color: 'white', opacity: 0.8, marginTop: '10px', fontStyle: 'italic', textAlign: 'center'}}>
            The chart shows POV scene distribution across characters
          </Text>
        </Box>
        
        <Box p="3" style={{backgroundColor: '#2c3839', borderRadius: '8px', marginTop: '10px'}}>
          <Text size="3" weight="bold" style={{color: 'white', marginBottom: '10px'}}>
            Character Analysis Insights
          </Text>
          <ul style={{color: 'white', opacity: 0.8, paddingLeft: '20px'}}>
            <li>Characters with more POV scenes are generally main characters</li>
            <li>Balance POV scenes for multi-viewpoint stories</li>
            <li>Characters with few POV scenes may be supporting characters</li>
            <li>Consider POV distribution across your narrative to achieve desired focus</li>
          </ul>
        </Box>
      </Flex>
      
      <style jsx>{`
        .sidebar-analytics-characters {
          width: 900px !important;
        }
      `}</style>
    </div>
  );
};

export default SidebarAnalyticsCharacters;
